import JWT from 'jwt-decode';

import {UserDataModel} from '../Admin/UserData.Model';

interface JWTPayload {
	exp: number;
	isAdmin: boolean;
	email: string;
	originalEmail: string;
}

interface LoginResponse extends Response {
	success: boolean;
	message?: string;
	token?: string;
}

export interface IAPIResponse<T> {
	success: boolean;
	data?: T;
	message?: string;
	error?: string;
}

class AuthService {
	public constructor() {
		this.fetch = this.fetch.bind(this);
		this.login = this.login.bind(this);
	}

	public async getAllUsers(): Promise<UserDataModel[]> {
		return this.fetch<IAPIResponse<UserDataModel[]>>('/api/users/', {
			method: 'GET',
		}).then((response): UserDataModel[] => {
			if (response.success) {
				if (response.data) {
					return response.data;
				} else {
					return [];
				}
			} else if (response.message) {
				throw new Error(response.message);
			} else {
				throw new Error('Unkown Error');
			}
		});
	}

	public login(email: string, password: string): Promise<LoginResponse> {
		return this.fetch<LoginResponse>(`/api/users/login`, {
			method: 'POST',
			body: JSON.stringify({
				email,
				password,
			}),
		}).then((response: LoginResponse): Promise<LoginResponse> => {
			if (response.success && response.token !== undefined) {
				this.setToken(response.token);
			} else if (response.message) {
				throw new Error(response.message);
			} else {
				throw new Error('Unknown Error');
			}
			return Promise.resolve(response);
		});
	}

	public loginAs(email: string): Promise<LoginResponse> {
		return this.fetch<LoginResponse>(`/api/users/loginAsUser`, {
			method: 'POST',
			body: JSON.stringify({
				email,
			}),
		}).then((response: LoginResponse): Promise<LoginResponse> => {
			if (response.success && response.token !== undefined) {
				this.setToken(response.token);
			} else if (response.message) {
				throw new Error(response.message);
			} else {
				throw new Error('Unknown Error');
			}
			return Promise.resolve(response);
		});
	}

	public loggedIn(): boolean {
		const token = this.getToken();
		return !!token && !this.isTokenExpired(token);
	}

	public isAdmin(): boolean {
		const token = this.getToken();

		if (!token) {
			return false;
		}

		try {
			const decoded: JWTPayload = JWT(token);
			return decoded.isAdmin;
		} catch (_err) {
			return false;
		}
	}

	public getUserEmail(): string {
		const token = this.getToken();

		if (!token) {
			return '';
		}

		try {
			const decoded: JWTPayload = JWT(token);
			return decoded.email || '';
		} catch (_err) {
			return '';
		}
	}

	public getOriginalEmail(): string {
		const token = this.getToken();

		if (!token) {
			return '';
		}

		try {
			const decoded: JWTPayload = JWT(token);
			return decoded.originalEmail || '';
		} catch (_err) {
			return '';
		}
	}

	public isTokenExpired(token: string): boolean {
		try {
			const decoded: JWTPayload = JWT(token);
			if (decoded.exp < Date.now() / 1000) {
				return true;
			} else return false;
		} catch (_err) {
			return false;
		}
	}

	private setToken(idToken: string): void {
		localStorage.setItem('id_token', idToken);
	}

	private getToken(): string | null {
		return localStorage.getItem('id_token');
	}

	public logout(): void {
		localStorage.removeItem('id_token');
	}

	public fetch<T>(url: string, options: object): Promise<T> {
		const headers: Record<string, string> = {
			//'Accept': 'application/json',
			'Content-Type': 'application/json',
		};

		if (this.loggedIn()) {
			headers['Authorization'] = 'Bearer ' + (this.getToken() ?? '');
		}

		return fetch(url, {headers, ...options})
			.then(this.checkStatus.bind(this))
			.then((response): Promise<T> => response.json() as Promise<T>);
	}

	public createOrChangeUser(user: UserDataModel): Promise<string> {
		return this.fetch<string>(`/api/users/${user._id ?? 'new'}`, {
			method: 'POST',
			body: JSON.stringify(user),
		});
	}

	public loadUser(user: UserDataModel): Promise<UserDataModel> {
		if (!user._id) {
			throw new Error('No User specified');
		}

		return this.fetch<UserDataModel>(`/api/users/${user._id}`, {
			method: 'GET',
		});
	}

	public deleteUser(user: UserDataModel): Promise<string> {
		if (!user._id) {
			throw new Error('No User specified');
		}

		return this.fetch<string>(`/api/users/${user._id}`, {
			method: 'DELETE',
			body: JSON.stringify(user),
		});
	}

	public async me(): Promise<UserDataModel> {
		const result = await this.fetch<IAPIResponse<UserDataModel>>(
			'/api/users/me',
			{
				method: 'GET',
			}
		);

		if (!result.data) {
			throw new Error('Failed loading User');
		}

		return result.data;
	}

	private checkStatus(response: Response): Response {
		if (response.status >= 200 && response.status < 300) {
			return response;
		} else {
			if (response.status === 401) {
				//not logged in
				//redirect to login
				this.logout();
				window.location.replace('/login');
			}

			throw new Error(response.statusText);
		}
	}
}

export default AuthService;
